import { isEmpty, isEqual } from 'bold-ui'
import { resolveName } from 'components/form'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { MutableRefObject, useCallback, useState } from 'react'
import { useForm } from 'react-final-form'
import { metaPath } from 'util/metaPath'

import {
  getNextPerguntaStatus,
  getPerguntaAtivaInfo,
  getPerguntasWithData,
  getPreviousPerguntaStatus,
  initializeAplicacaoValues,
  pruneAplicacao,
  setLastPerguntaToNextStatus,
} from '../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import {
  ConsultaSeguimentoFieldModel,
  ConsultaSeguimentoPerguntaStatusEnum,
  ConsultaSeguimentoSelectionState,
} from '../model-consultaSeguimento'

interface UseConsultaSeguimentoAplicacao {
  modalContentRef: MutableRefObject<HTMLDivElement>
  backQuestionButtonRef: MutableRefObject<HTMLButtonElement>
  backToAplicacaoButtonRef: MutableRefObject<HTMLButtonElement>
  backToSelecaoButtonRef: MutableRefObject<HTMLButtonElement>
  valueInCache: ConsultaSeguimentoFieldModel
  mChatPerguntasFalhas: MChatPerguntaEnum[]
  onConfirmSelectionChanges: (
    perguntasAnteriores: MChatPerguntaEnum[],
    perguntasAtuais: MChatPerguntaEnum[],
    perguntasObrigatorias: MChatPerguntaEnum[],
    perguntasComRespostas: MChatPerguntaEnum[]
  ) => Promise<boolean>
  onStart: () => void
  onFinish: () => void
  onSave: (values: ConsultaSeguimentoFieldModel) => void
}

const path = metaPath<ConsultaSeguimentoFieldModel>()

export function useConsultaSeguimentoAplicacao(props: Readonly<UseConsultaSeguimentoAplicacao>) {
  const {
    modalContentRef,
    backQuestionButtonRef,
    backToAplicacaoButtonRef,
    backToSelecaoButtonRef,
    valueInCache,
    mChatPerguntasFalhas,
    onConfirmSelectionChanges,
    onStart,
    onFinish,
    onSave,
  } = props

  const hasValueInCache = !isEmpty(valueInCache)

  const { change, getState } = useForm<ConsultaSeguimentoFieldModel>()

  const [selectionState, setSelectionState] = useState<ConsultaSeguimentoSelectionState>({
    failed: false,
    values: hasValueInCache ? valueInCache.perguntasSelecionadas : [],
  })

  const clearStartFailed = useCallback(() => {
    setSelectionState((prevState) => ({
      failed: false,
      values: prevState.values,
    }))
  }, [])

  const confirmChanges = useCallback(async () => {
    const { values } = getState()

    const sortedPerguntasSelecionadas = [...(values.perguntasSelecionadas ?? [])].sort((a, b) => a.localeCompare(b))
    const sortedSelectionStateValues = [...selectionState.values].sort((a, b) => a.localeCompare(b))
    const hasChanged =
      selectionState.values.length > 0 && !isEqual(sortedPerguntasSelecionadas, sortedSelectionStateValues)

    if (!hasChanged) return true

    const perguntasComRespostas = getPerguntasWithData(values.aplicacao)

    return await onConfirmSelectionChanges(
      selectionState.values,
      values.perguntasSelecionadas,
      mChatPerguntasFalhas,
      perguntasComRespostas
    )
  }, [getState, selectionState.values, onConfirmSelectionChanges, mChatPerguntasFalhas])

  const applySelectionChanges = useCallback(() => {
    const { values } = getState()

    change('', {
      perguntasSelecionadas: [...values.perguntasSelecionadas].sort((a, b) => a.localeCompare(b)),
      aplicacao: initializeAplicacaoValues(values.perguntasSelecionadas, values.aplicacao),
    })

    setSelectionState({
      failed: false,
      values: values.perguntasSelecionadas,
    })
  }, [change, getState])

  const handleGoToNextPergunta = useCallback(
    (values: ConsultaSeguimentoFieldModel, indexOfPerguntaAtiva: number, perguntaAtiva: string) => {
      const valuesOfPerguntaAtiva = values.aplicacao[perguntaAtiva]
      const nextPergunta = values.perguntasSelecionadas[indexOfPerguntaAtiva + 1]

      change(resolveName(path.aplicacao[perguntaAtiva]), {
        ...valuesOfPerguntaAtiva,
        status: getNextPerguntaStatus(valuesOfPerguntaAtiva.status),
      })

      change(resolveName(path.aplicacao[nextPergunta]), {
        ...values.aplicacao[nextPergunta],
        status: ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO,
      })

      setTimeout(() => backQuestionButtonRef.current.focus())
    },
    [backQuestionButtonRef, change]
  )

  const handleFinishAplicacao = useCallback(
    (values: ConsultaSeguimentoFieldModel) => {
      const updatedAplicacao = setLastPerguntaToNextStatus(values.aplicacao)
      const prunedAplicacao = pruneAplicacao(updatedAplicacao)

      onSave({ ...values, aplicacao: prunedAplicacao })
      onFinish()
      setTimeout(() => backToAplicacaoButtonRef.current.focus())
    },
    [backToAplicacaoButtonRef, onFinish, onSave]
  )

  const start = useCallback(async () => {
    const { errors } = getState()

    const isInvalid = resolveName(path.perguntasSelecionadas) in errors

    if (isInvalid) {
      setSelectionState((prev) => ({
        failed: true,
        values: prev.values,
      }))
      modalContentRef.current.scrollTo({ behavior: 'smooth', top: 0 })
      return
    }

    const confirmed = await confirmChanges()
    if (!confirmed) return

    applySelectionChanges()
    onStart()
    setTimeout(() => backToSelecaoButtonRef.current?.focus())
  }, [getState, confirmChanges, applySelectionChanges, onStart, modalContentRef, backToSelecaoButtonRef])

  const goToNextQuestion = useCallback(() => {
    const { values, errors } = getState()

    const { perguntaAtiva, index: indexOfPerguntaAtiva, isLast: isLastPergunta } = getPerguntaAtivaInfo(
      values.aplicacao
    )

    const isInvalid = !!errors.aplicacao && resolveName(path.aplicacao[perguntaAtiva]) in errors.aplicacao
    if (isInvalid) return

    if (isLastPergunta) {
      handleFinishAplicacao(values)
    } else {
      handleGoToNextPergunta(values, indexOfPerguntaAtiva, perguntaAtiva)
    }
  }, [getState, handleFinishAplicacao, handleGoToNextPergunta])

  const goToPreviousQuestion = useCallback(() => {
    const { values } = getState()

    const { perguntaAtiva, index: indexOfPerguntaAtiva, isFirst: isFirstPergunta } = getPerguntaAtivaInfo(
      values.aplicacao
    )

    if (!isFirstPergunta) {
      const prevPergunta = values.perguntasSelecionadas[indexOfPerguntaAtiva - 1]

      change(resolveName(path.aplicacao), {
        ...values.aplicacao,
        [perguntaAtiva]: {
          ...values.aplicacao[perguntaAtiva],
          status: getPreviousPerguntaStatus(values.aplicacao[perguntaAtiva].status),
        },
        [prevPergunta]: {
          ...values.aplicacao[prevPergunta],
          status: getPreviousPerguntaStatus(values.aplicacao[prevPergunta].status),
        },
      })
    }
  }, [change, getState])

  return {
    selectionState,
    clearStartFailed,
    start,
    goToNextQuestion,
    goToPreviousQuestion,
  }
}
